// WHAT: Tab component for displaying day-by-day tracking overview
// WHY:  Provides chronological view of tracked time with daily aggregates
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: useFilter hook, core.invoke, debugProductivity
// RISKS:
//   - NOTE: Period normalization may not handle all period formats
//   - NOTE: Date aggregation may have timezone issues
//   - NOTE: Top workspace calculation may not be accurate for ties
//   - NOTE: Loading state may flash on filter changes

import { core } from "@tauri-apps/api";
import { type FC, useEffect, useMemo, useState } from "react";

import { Card } from "@/components/ui";
import { debugProductivity } from "@/domains/logging/utils/debugLog";

import { useFilter } from "../../components/molecules/FilterContextProvider.shared";

/**
 * Session interface
 */
interface Session {
  /** Aspect name */
  aspect?: string;
  /** Duration in minutes (optional) */
  duration_minutes?: null | number;
  /** End time (ISO, optional) */
  end_time?: null | string;
  /** Optional notes */
  notes?: null | string;
  /** Start time (ISO) */
  start_time: string;
  /** Workspace name */
  workspace?: string;
}

/**
 * Day aggregate interface
 */
interface DayAggregate {
  /** Date key (YYYY-MM-DD) */
  dateKey: string;
  /** Date label (e.g., "10.10.2024") */
  dateLabel: string;
  /** Top workspace (optional) */
  topWorkspace?: { minutes: number; name: string };
  /** Total minutes */
  totalMinutes: number;
  /** Weekday label (e.g., "Montag") */
  weekdayLabel: string;
}

/**
 * WHAT:
 *   Gets start of day (00:00:00).
 *
 * WHY:
 *   Enables consistent date range calculations.
 *
 * @param d
 *
 * @returns {Date} Date with time set to 00:00:00.000
 *
 */
function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * WHAT:
 *   Gets end of day (23:59:59.999).
 *
 * WHY:
 *   Enables consistent date range calculations.
 *
 * @param d
 *
 * @returns {Date} Date with time set to 23:59:59.999
 *
 */
function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/**
 * WHAT:
 *   Normalizes period string to backend format.
 *
 * WHY:
 *   Handles various period format variations for backend compatibility.
 *
 * @param p
 *
 * @returns {string | undefined} Normalized period string or undefined
 *
 */
function normalizePeriod(p: unknown): string | undefined {
  if (!p) {
    return undefined;
  }
  const s =
    typeof p === "string" || typeof p === "number" || typeof p === "boolean"
      ? String(p)
      : JSON.stringify(p).trim().toLowerCase().replaceAll(/\s+/g, "_").replaceAll("-", "_");
  switch (s) {
    case "last_week":
    case "lastweek": {
      return "last_week";
    }
    case "last_7_days":
    case "last7days": {
      return "last_7_days";
    }
    case "last_month":
    case "lastmonth": {
      return "last_month";
    }
    case "last_30_days":
    case "last30days": {
      return "last_30_days";
    }
    case "last_year":
    case "lastyear": {
      return "last_year";
    }
    case "this_week":
    case "week": {
      return "week";
    }
    case "this_month":
    case "month": {
      return "month";
    }
    case "this_year":
    case "year": {
      return "year";
    }
    case "today": {
      return "today";
    }
    default: {
      return s;
    }
  }
}

/**
 * WHAT:
 *   Computes date range for period-based queries.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting period calculation logic.
 *
 * @param {string | undefined} normalizedPeriod - Normalized period string
 *
 * @param {Date} now - Current date
 *
 * @returns {{ from?: Date; to?: Date }} Date range object
 *
 */
function computePeriodRange(
  normalizedPeriod: string | undefined,
  now: Date,
): { from?: Date; to?: Date } {
  switch (normalizedPeriod) {
    case "today": {
      return { from: startOfDay(now), to: endOfDay(now) };
    }
    case "last_week": {
      const to = endOfDay(new Date(now));
      to.setDate(now.getDate() - 1);
      const from = startOfDay(new Date(to));
      from.setDate(to.getDate() - 6);
      return { from, to };
    }
    case "week": {
      const day = now.getDay() || 7;
      const monday = new Date(now);
      monday.setDate(now.getDate() - (day - 1));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { from: startOfDay(monday), to: endOfDay(sunday) };
    }
    case "last_7_days": {
      const to = endOfDay(now);
      const from = startOfDay(new Date(now));
      from.setDate(now.getDate() - 6);
      return { from, to };
    }
    case "month": {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: startOfDay(first), to: endOfDay(last) };
    }
    case "last_month": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfDay(first), to: endOfDay(last) };
    }
    case "last_30_days": {
      const to = endOfDay(now);
      const from = startOfDay(new Date(now));
      from.setDate(now.getDate() - 29);
      return { from, to };
    }
    case "year": {
      const first = new Date(now.getFullYear(), 0, 1);
      const last = new Date(now.getFullYear(), 11, 31);
      return { from: startOfDay(first), to: endOfDay(last) };
    }
    case "last_year": {
      const first = new Date(now.getFullYear() - 1, 0, 1);
      const last = new Date(now.getFullYear() - 1, 11, 31);
      return { from: startOfDay(first), to: endOfDay(last) };
    }
    default: {
      return {};
    }
  }
}

/**
 * WHAT:
 *   Computes date range from period or dateFilter.
 *
 * WHY:
 *   Converts period/dateFilter to concrete date range for backend queries.
 *
 * @param period
 *
 * @param dateFilter
 *
 * @returns {{ from?: Date; to?: Date }} Date range object
 *
 */
/**
 * WHAT:
 *   Computes date range from period or dateFilter.
 *
 * WHY:
 *   Converts period/dateFilter to concrete date range for backend queries.
 *
 * NOTE: Cognitive complexity is 20 (limit: 15) due to multiple dateFilter format handling
 * (array, object, Date, string) and period-based calculations. The function handles various input
 * formats robustly. Further splitting would reduce maintainability without significant benefit.
 *
 * @param period
 *
 * @param dateFilter
 *
 * @returns {{ from?: Date; to?: Date }} Date range object
 *
 */
/* eslint-disable sonarjs/cognitive-complexity -- Reason: See above note */
/* eslint-disable jsdoc/require-jsdoc -- Reason: JSDoc is above eslint-disable comment */
function computeRange(period: unknown, dateFilter: unknown): { from?: Date; to?: Date } {
  if (dateFilter) {
    if (Array.isArray(dateFilter) && dateFilter.length > 0) {
      const date0 = dateFilter[0] as Date | number | string | undefined;
      const date1 = dateFilter[1] as Date | number | string | undefined;
      const from = date0 ? startOfDay(new Date(date0)) : undefined;
      const to = date1 ? endOfDay(new Date(date1)) : undefined;
      return { ...(from && { from }), ...(to && { to }) };
    }
    const dateFilterObject = dateFilter as null | {
      end?: Date | string;
      from?: Date | string;
      start?: Date | string;
      to?: Date | string;
    };
    const fromValue = dateFilterObject?.start ?? dateFilterObject?.from;
    const toValue = dateFilterObject?.end ?? dateFilterObject?.to;
    if (fromValue || toValue) {
      return {
        ...(fromValue && { from: startOfDay(new Date(fromValue)) }),
        ...(toValue && { to: endOfDay(new Date(toValue)) }),
      };
    }
    if (dateFilter instanceof Date || typeof dateFilter === "string") {
      const d = new Date(dateFilter);
      return { from: startOfDay(d), to: endOfDay(d) };
    }
  }
  const now = new Date();
  const p = normalizePeriod(period);
  return computePeriodRange(p, now);
}
/* eslint-enable jsdoc/require-jsdoc */
/* eslint-enable sonarjs/cognitive-complexity */

/**
 * WHAT:
 *   Calculates duration in minutes between two ISO timestamps.
 *
 * WHY:
 *   Converts ISO timestamp difference to minutes for display.
 *
 * @param startIso
 *
 * @param endIso
 *
 * @returns {number} Duration in minutes or 0 if invalid
 *
 */
function toMinutes(startIso: string, endIso?: null | string): number {
  if (!endIso) {
    return 0;
  }
  const s = new Date(startIso).getTime();
  const e = new Date(endIso).getTime();
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) {
    return 0;
  }
  return Math.round((e - s) / 60_000);
}

/**
 * WHAT:
 *   Formats total minutes as hours and minutes string.
 *
 * WHY:
 *   Provides human-readable time format (e.g., "2h 30m" or "3h").
 *
 * @param totalMinutes
 *
 * @returns {string} Formatted time string
 *
 */
function formatHm(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m === 0 ? `${String(h)}h` : `${String(h)}h ${String(m)}m`;
}

/**
 * WHAT:
 *   Gets German weekday name for a date.
 *
 * WHY:
 *   Provides localized weekday display.
 *
 * @param date
 *
 * @returns {string} German weekday name
 *
 */
function germanWeekday(date: Date): string {
  return date.toLocaleDateString("de-DE", { weekday: "long" });
}

/**
 * WHAT:
 *   Formats date in German locale.
 *
 * WHY:
 *   Provides localized date display.
 *
 * @param date
 *
 * @returns {string} Formatted date string in German locale
 *
 */
function germanDate(date: Date): string {
  return date.toLocaleDateString("de-DE");
}

/**
 * WHAT:
 *   Gets year-month key (YYYY-MM).
 *
 * WHY:
 *   Enables grouping by month.
 *
 * @param date
 *
 * @returns {string} Year-month key in format YYYY-MM
 *
 */
function getYearMonthKey(date: Date): string {
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * WHAT:
 *   Calculates ISO week number.
 *
 * WHY:
 *   Enables grouping by ISO week.
 *
 * @param date
 *
 * @returns {number} ISO week number
 *
 */
function getIsoWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/**
 * WHAT:
 *   Tab component that displays day-by-day tracking overview with monthly
 *   and weekly grouping.
 *
 * WHY:
 *   Enables users to review their tracking history in a chronological view
 *   with daily aggregates and workspace breakdowns.
 *
 * BEHAVIOR:
 *   - Fetches tracking sessions from backend
 *   - Normalizes session data (handles various response formats)
 *   - Aggregates sessions by day
 *   - Groups days by month and ISO week
 *   - Displays daily totals and top workspace
 *   - Updates when filter changes
 *
 * RISKS:
 *   - NOTE: Period normalization may not handle all period formats
 *   - NOTE: Date aggregation may have timezone issues
 *   - NOTE: Top workspace calculation may not be accurate for ties
 *   - NOTE: Loading state may flash on filter changes
 *   - NOTE: Response format handling is complex - may miss edge cases
 *
 * @returns {JSX.Element} Day tracking tab component
 *
 */
export const DayTrack: FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const filterResult = useFilter();
  const dateFilter = filterResult.dateFilter;
  const period = filterResult.period;

  // Reason: Cognitive complexity is 18 (limit: 15) due to complex async execution flow with
  // response parsing, session normalization, and error handling. The complexity is primarily
  // from handling various response formats and session normalization, which is acceptable for
  // a data fetching function.
  /* eslint-disable sonarjs/cognitive-complexity -- Reason: See comment above */
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError(undefined);
      try {
        debugProductivity("debug", "timetracq", "DayTrack.fetch start", {
          dateFilter,
          file: "DayTrack.tsx",
          fn: "run",
          period,
        });
        // NOTE: Backend Command: fetch_tracking_sessions (siehe invoke_handler)
        const res = await core.invoke("fetch_tracking_sessions");
        debugProductivity("debug", "timetracq", "DayTrack.fetch raw response", {
          file: "DayTrack.tsx",
          fn: "run",
          typeof: typeof res,
        });
        if (!mounted) {
          return;
        }
        // NOTE: Handle various response formats (array, object with sessions, JSON string)
        let array: Session[] = [];
        if (Array.isArray(res)) {
          array = res as Session[];
        } else if (
          res &&
          typeof res === "object" &&
          "sessions" in res &&
          Array.isArray((res as { sessions: unknown }).sessions)
        ) {
          array = (res as { sessions: Session[] }).sessions;
        } else if (typeof res === "string") {
          try {
            const parsed = JSON.parse(res) as unknown;
            if (Array.isArray(parsed)) {
              array = parsed as Session[];
            } else if (
              parsed &&
              typeof parsed === "object" &&
              "sessions" in parsed &&
              Array.isArray((parsed as { sessions: unknown }).sessions)
            ) {
              array = (parsed as { sessions: Session[] }).sessions;
            }
          } catch (error_) {
            const errorMessage = error_ instanceof Error ? error_.message : String(error_);
            debugProductivity("error", "timetracq", "DayTrack.parse error", {
              error: errorMessage,
              file: "DayTrack.tsx",
              fn: "run",
            });
          }
        }
        debugProductivity("debug", "timetracq", "DayTrack.normalize length", {
          count: array.length,
          file: "DayTrack.tsx",
          fn: "run",
        });
        // NOTE: Normalize session data (handle various field name variations)
        const norm: Session[] = array.map((r: unknown) => {
          const session = r as {
            aspect?: string;
            child?: string;
            duration_minutes?: null | number;
            end_time?: null | string;
            notes?: null | string;
            parent?: string;
            start_time: string;
            workspace?: string;
          };
          const aspectValue = session.aspect ?? session.child;
          const workspaceValue = session.workspace ?? session.parent;
          return {
            ...(aspectValue && { aspect: aspectValue }),
            duration_minutes:
              session.duration_minutes ??
              (
                session as {
                  duration?: number;
                  duration_minutes_total?: number;
                }
              ).duration_minutes_total ??
              (
                session as {
                  duration?: number;
                  duration_minutes_total?: number;
                }
              ).duration ??
              null,
            ...(session.end_time && { end_time: session.end_time }),
            ...(session.notes && { notes: session.notes }),
            start_time: session.start_time,
            ...(workspaceValue && { workspace: workspaceValue }),
          };
        });
        setSessions(norm);
        debugProductivity("debug", "timetracq", "DayTrack.setSessions done", {
          count: norm.length,
          file: "DayTrack.tsx",
          fn: "run",
        });
      } catch (error_: unknown) {
        debugProductivity("error", "timetracq", "DayTrack.fetch error", {
          error: String(error_),
          file: "DayTrack.tsx",
          fn: "run",
        });
        if (mounted) {
          setError("Fehler beim Laden der Sessions");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [period, dateFilter]);
  /* eslint-enable sonarjs/cognitive-complexity */

  const grouped = useMemo(() => {
    const { from, to } = computeRange(period, dateFilter);
    debugProductivity("debug", "timetracq", "DayTrack.group start", {
      file: "DayTrack.tsx",
      fn: "group",
      from: from?.toISOString(),
      period: normalizePeriod(period),
      sessions: sessions.length,
      to: to?.toISOString(),
    });
    // NOTE: Aggregate per day, then group by month and ISO week
    const byDay = new Map<
      string,
      { date: Date; total: number; workspaceMinutes: Map<string, number> }
    >();
    for (const s of sessions) {
      const d = new Date(s.start_time);
      // NOTE: Filter by date range
      if (from && d < from) {
        continue;
      }
      if (to && d > to) {
        continue;
      }
      const key = d.toISOString().slice(0, 10);
      const dur: number = s.duration_minutes ?? toMinutes(s.start_time, s.end_time);
      const entry = byDay.get(key) ?? {
        date: d,
        total: 0,
        workspaceMinutes: new Map<string, number>(),
      };
      entry.total += dur;
      const ws = s.workspace ?? "Unbekannt";
      const currentMinutes: number = entry.workspaceMinutes.get(ws) ?? 0;
      const newMinutes: number = currentMinutes + dur;
      entry.workspaceMinutes.set(ws, newMinutes);
      byDay.set(key, entry);
    }

    // NOTE: Convert to DayAggregate array with top workspace
    type DayEntry = [string, { date: Date; total: number; workspaceMinutes: Map<string, number> }];
    // Reason: TypeScript incorrectly infers Map.entries() as error type due to complex nested generics

    const dayEntriesRaw = [];
    for (const entry of byDay.entries()) {
      dayEntriesRaw.push(entry);
    }
    const dayEntries = dayEntriesRaw as DayEntry[];
    const days: DayAggregate[] = dayEntries

      .map(([key, v]: DayEntry): DayAggregate => {
        let topWs: undefined | { minutes: number; name: string };
        for (const [name, minutes] of v.workspaceMinutes.entries()) {
          if (!topWs || minutes > topWs.minutes) {
            topWs = { minutes, name };
          }
        }
        const dt: Date = v.date;
        return {
          dateKey: key,
          dateLabel: germanDate(dt),
          ...(topWs ? { topWorkspace: topWs } : {}),
          totalMinutes: v.total,
          weekdayLabel: germanWeekday(dt),
        };
      })
      .toSorted((a: DayAggregate, b: DayAggregate) => a.dateKey.localeCompare(b.dateKey));

    // NOTE: Group into {month -> {week -> days[]}}
    const months = new Map<string, Map<number, DayAggregate[]>>();
    for (const d of days) {
      const dt = new Date(d.dateKey);
      const mKey = getYearMonthKey(dt);
      const w = getIsoWeek(dt);
      const byWeek = months.get(mKey) ?? new Map<number, DayAggregate[]>();
      const array = byWeek.get(w) ?? [];
      array.push(d);
      byWeek.set(w, array);
      months.set(mKey, byWeek);
    }
    debugProductivity("debug", "timetracq", "DayTrack.group done", {
      file: "DayTrack.tsx",
      fn: "group",
      months: months.size,
    });
    return months;
  }, [sessions, period, dateFilter]);

  if (loading) {
    return (
      <Card className="bg-slate-800/30 border-slate-600/50" padding="lg" variant="elevated">
        <div className="h-32 flex items-center justify-center text-slate-400">
          Lade Tagesübersicht…
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-slate-800/30 border-slate-600/50" padding="lg" variant="elevated">
        <div className="text-red-400">{error}</div>
      </Card>
    );
  }

  if (grouped.size === 0) {
    return (
      <Card className="bg-slate-800/30 border-slate-600/50" padding="lg" variant="elevated">
        <div className="h-24 flex items-center justify-center text-slate-400">
          Keine Daten im Zeitraum
        </div>
      </Card>
    );
  }

  type MonthEntry = [string, Map<number, DayAggregate[]>];
  const monthEntries: MonthEntry[] = [...grouped.entries()];

  return (
    <div className="space-y-6">
      {monthEntries.map(([monthKey, weeks]: MonthEntry) => {
        type WeekEntry = [number, DayAggregate[]];
        // Reason: TypeScript incorrectly infers Map.entries() as error type due to complex nested generics

        const weekEntriesRaw = [];
        for (const entry of weeks.entries()) {
          weekEntriesRaw.push(entry);
        }
        const weekEntries = weekEntriesRaw as WeekEntry[];
        const sortedWeeks: WeekEntry[] = weekEntries.toSorted(
          ([weekA]: WeekEntry, [weekB]: WeekEntry): number => weekA - weekB,
        );

        return (
          <Card
            className="bg-slate-800/30 border-slate-600/50"
            key={monthKey}
            padding="lg"
            variant="elevated"
          >
            <div className="space-y-4">
              <div className="text-white text-xl font-semibold">
                {new Date(monthKey + "-01").toLocaleDateString("de-DE", {
                  month: "long",
                  year: "numeric",
                })}
              </div>
              <div className="space-y-3">
                {sortedWeeks.map(([week, days]: WeekEntry) => (
                  <div key={week}>
                    <div className="text-slate-300 font-medium mb-2">Woche {String(week)}</div>
                    <div className="space-y-2">
                      {days.map((d: DayAggregate) => (
                        <div
                          className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-700/30 border border-slate-600/50"
                          key={d.dateKey}
                        >
                          <div className="text-white">
                            {d.weekdayLabel} {d.dateLabel}
                          </div>
                          <div className="text-slate-300">
                            {formatHm(d.totalMinutes)}
                            {d.topWorkspace && d.topWorkspace.minutes > 0 ? (
                              <span className="ml-2 text-slate-400">
                                {d.topWorkspace.name}: {formatHm(d.topWorkspace.minutes)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
